/**
 * L'heure d'un spot.
 *
 * Un evenement se termine a 19 h **a Marrakech**, pas a 19 h sur le
 * telephone de celui qui regarde. Tant que tout le monde etait dans la
 * meme ville, la difference ne se voyait pas ; elle se voit des qu'un
 * organisateur voyage, et elle se verra sur chaque ville ouverte.
 *
 * Ce fichier est le seul endroit ou une date de spot se calcule. Aucun
 * `new Date(annee, mois, jour)` ailleurs : cette forme-la construit un
 * instant dans le fuseau de l'appareil, et c'est precisement le piege.
 */

/** Ce qu'il faut pour situer un evenement dans le temps. */
export interface Horaires {
  /** Date ISO du jour : "2026-08-14". */
  date: string;
  /** "18h30" ou "18:30". */
  startTime: string;
  endTime?: string;
}

/**
 * Le fuseau retenu quand on ne sait pas.
 *
 * Paris plutot que celui de l'appareil : au moins il est le meme pour
 * tout le monde, donc deux membres voient la meme chose. Un repli qui
 * suit l'appareil ferait diverger les ecrans en silence.
 */
const FUSEAU_PAR_DEFAUT = 'Europe/Paris';

/** Les fuseaux connus, poses au fil des lectures du catalogue. */
const fuseaux = new Map<string, string>();

/** Retient le fuseau d'un spot. Appele par la couche donnee. */
export function retenirFuseau(serverId: string, timezone: string | undefined): void {
  if (timezone) fuseaux.set(serverId, timezone);
}

/** Le fuseau d'un spot, ou le repli commun. */
export function fuseauDe(serverId: string): string {
  return fuseaux.get(serverId) ?? FUSEAU_PAR_DEFAUT;
}

/** Minutes depuis minuit, a partir de "18h30" ou "18:30". */
export function minutesDe(heure: string | undefined): number {
  const trouve = /^(\d{1,2})\s*[h:]\s*(\d{2})?/.exec((heure ?? '').trim());
  if (!trouve) return 0;
  return Number(trouve[1]) * 60 + Number(trouve[2] ?? 0);
}

/**
 * Le decalage d'un fuseau a un instant donne, en millisecondes.
 *
 * On demande a `Intl` l'heure murale de cet instant dans le fuseau, on la
 * relit comme si elle etait en UTC, et l'ecart entre les deux est le
 * decalage. C'est la seule facon d'obtenir un decalage exact — heure
 * d'ete comprise — sans embarquer une base de fuseaux.
 */
function decalage(timezone: string, instant: number): number {
  const parties = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));

  const lu: Record<string, number> = {};
  for (const partie of parties) {
    if (partie.type !== 'literal') lu[partie.type] = Number(partie.value);
  }

  const commeUtc = Date.UTC(
    lu.year,
    lu.month - 1,
    lu.day,
    // Minuit se lit "24" dans certaines locales : on le ramene a zero.
    lu.hour % 24,
    lu.minute,
    lu.second,
  );
  return commeUtc - instant;
}

/**
 * L'instant correspondant a une heure murale dans un fuseau.
 *
 * Deux passes. La premiere prend l'heure pour de l'UTC et retire le
 * decalage ; elle se trompe autour d'un changement d'heure, ou le
 * decalage retire est celui de l'autre cote de la bascule. La seconde
 * recalcule le decalage a l'instant approche, ce qui suffit partout sauf
 * dans l'heure ambigue d'un retour a l'heure d'hiver — la, on retient une
 * des deux lectures, toujours la meme.
 */
function instantDe(timezone: string, annee: number, mois: number, jour: number, minutes: number) {
  const naif = Date.UTC(annee, mois - 1, jour, Math.floor(minutes / 60), minutes % 60);
  const approche = naif - decalage(timezone, naif);
  return naif - decalage(timezone, approche);
}

/** Le jour d'un evenement, decoupe. `null` si la date est inexploitable. */
function jourDe(date: string): [number, number, number] | null {
  const [annee, mois, jour] = date.split('-').map(Number);
  if (!annee || !mois || !jour) return null;
  return [annee, mois, jour];
}

/**
 * L'instant ou l'evenement commence, dans le fuseau du spot.
 */
export function debutDe(horaires: Horaires, timezone: string): number {
  const jour = jourDe(horaires.date);
  if (!jour) return Number.POSITIVE_INFINITY;
  return instantDe(timezone, jour[0], jour[1], jour[2], minutesDe(horaires.startTime));
}

/**
 * L'instant ou l'evenement se termine, dans le fuseau du spot.
 *
 * Une fin plus petite ou egale au debut passe au lendemain : c'est le cas
 * des soirees a cheval sur minuit. Une fin absente vaut la fin du jour.
 */
export function finDe(horaires: Horaires, timezone: string): number {
  const jour = jourDe(horaires.date);
  if (!jour) return Number.POSITIVE_INFINITY;

  const chiffree = /\d/.test(horaires.endTime ?? '');
  const fin = chiffree ? minutesDe(horaires.endTime) : 24 * 60;
  const lendemain = chiffree && fin <= minutesDe(horaires.startTime) ? 1 : 0;

  return instantDe(timezone, jour[0], jour[1], jour[2] + lendemain, fin);
}

/** L'horaire de fin est franchi, a l'heure du spot. */
export function estPasse(horaires: Horaires, timezone: string): boolean {
  return finDe(horaires, timezone) < Date.now();
}

/** Deux creneaux se chevauchent, a l'heure du spot. */
export function seChevauchent(a: Horaires, b: Horaires, timezone: string): boolean {
  return debutDe(a, timezone) < finDe(b, timezone) && debutDe(b, timezone) < finDe(a, timezone);
}

/**
 * Le mois calendaire courant du spot : "2026-08".
 *
 * C'est ce que « ce mois » veut dire dans le classement. A minuit le
 * premier du mois a Marrakech, le classement repart — meme pour un
 * membre qui consulte depuis Paris.
 */
export function moisCourant(timezone: string, instant = Date.now()): string {
  const parties = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date(instant));

  const lu: Record<string, string> = {};
  for (const partie of parties) {
    if (partie.type !== 'literal') lu[partie.type] = partie.value;
  }
  return `${lu.year}-${lu.month}`;
}
